import React, { useContext, useMemo, useState } from 'react';
import { Joyride, STATUS } from 'react-joyride';
import { useTranslation } from 'react-i18next';
import AuthContext from '../context/AuthContext';
import { ONBOARDING_STEPS } from '../config/onboardingSteps';
import { isSeen, markSeen } from '../utils/onboardingSeen';
import './OnboardingTour.css';

// react-joyride exige que el beacon custom renderice un <span> (va dentro de su <button>).
const StepBeacon = ({ step }) => {
    const { t } = useTranslation();
    return (
        <span className="onboarding-beacon">
            <span className="onboarding-beacon__label">
                {t('common.stepLabel', { number: step.data?.stepNumber })}
            </span>
            <span className="onboarding-beacon__outer" />
            <span className="onboarding-beacon__inner" />
        </span>
    );
};

/**
 * Tutorial contextual (spotlight): resalta el botón exacto que resuelve el paso de
 * onboarding activo (ver src/config/onboardingSteps.js — un paso a la vez, en el mismo
 * orden que ya siguen los banners de Layout.js). Se marca como visto en localStorage al
 * cerrarlo, para no volver a mostrarlo aunque el paso siga sin completarse — el banner
 * correspondiente sigue ahí como recordatorio persistente.
 */
const OnboardingTour = () => {
    const { user, business } = useContext(AuthContext);
    const { t } = useTranslation();
    // Se incrementa al cerrar un spotlight, para forzar reevaluar si hay otro pendiente.
    const [dismissedTick, setDismissedTick] = useState(0);

    const activeStep = useMemo(() => {
        if (!user) return null;
        const step = ONBOARDING_STEPS.find((s) => s.isActive(user, business));
        if (!step || isSeen(user.id, step.key)) return null;
        return step;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, business, dismissedTick]);

    if (!activeStep) return null;

    const steps = [{
        target: activeStep.target,
        content: (
            <div>
                <strong>{t(activeStep.titleKey)}</strong>
                <p className="mb-0 mt-1">{t(activeStep.contentKey)}</p>
            </div>
        ),
        data: { stepNumber: activeStep.stepNumber },
        placement: 'bottom',
    }];

    const handleCallback = (data) => {
        if (data.status === STATUS.FINISHED || data.status === STATUS.SKIPPED) {
            markSeen(user.id, activeStep.key);
            setDismissedTick((v) => v + 1);
        }
    };

    return (
        <Joyride
            key={activeStep.key}
            beaconComponent={StepBeacon}
            steps={steps}
            run
            continuous={false}
            showSkipButton={false}
            showProgress={false}
            locale={{ close: t('common.gotIt') }}
            styles={{ options: { primaryColor: '#0d6efd', zIndex: 10000 } }}
            callback={handleCallback}
        />
    );
};

export default OnboardingTour;
